import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plane, Loader2, Download, ImagePlus, Camera, Check, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "theditch_members_token";

const PERSONALITY = [
  "House Trained", "Loves Other Dogs", "Quiet Indoors", "Doesn't Bark Excessively",
  "Loves Car Rides", "Professional Cuddle Expert", "Loves Children", "Friendly",
  "Doesn't Dig", "Sleeps Indoors", "Beach Lover", "Treat Enthusiast",
];

const EMPTY = {
  pet_name: "", breed: "", age: "", weight: "", colour: "", humans: "",
  photo_base64: null,
  microchipped: false, desexed: false, council_registered: false, vaccinations_current: false,
  personality: [],
  rented_before: "", rental_duration: "", damage: "", previous_landlord: "",
  vaccinated: false, flea_treatments: false, worm_treatments: false, healthy: false,
  vet_clinic: "",
  why_love_to_live: "",
  landlord_ref_name: "", landlord_ref_phone: "", landlord_ref_email: "", landlord_ref_notes: "",
  vet_ref_name: "", vet_ref_phone: "", vet_ref_email: "", vet_ref_notes: "",
};

const DRAFT_KEY = "theditch_pet_resume_draft";

export default function PetResume({ mode = "members" }) {
  const isFree = mode === "free";
  const [token, setToken] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  // Free-mode email capture
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);

  // Auth + draft restore
  useEffect(() => {
    if (isFree) {
      // Public mode — no auth required
      try {
        const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
        if (draft) setForm({ ...EMPTY, ...draft });
      } catch (err) {
        console.warn("Pet Resume: could not restore draft from localStorage", err);
      }
      return;
    }
    const t = localStorage.getItem(STORAGE_KEY);
    if (!t) {
      window.location.href = "/members";
      return;
    }
    setToken(t);
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (draft) setForm({ ...EMPTY, ...draft });
    } catch (err) {
      console.warn("Pet Resume: could not restore draft from localStorage", err);
    }
  }, [isFree]);

  // Autosave draft (debounced via state effect)
  useEffect(() => {
    if (!isFree && !token) return;
    const id = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      } catch (err) {
        console.warn("Pet Resume: could not save draft to localStorage", err);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [form, token, isFree]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePersonality = (opt) => {
    setForm((f) => ({
      ...f,
      personality: f.personality.includes(opt)
        ? f.personality.filter((x) => x !== opt)
        : [...f.personality, opt],
    }));
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Photo is too big — keep it under 6MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("photo_base64")(reader.result);
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!form.pet_name.trim()) {
      toast.error("Please enter your pet's name first.");
      return;
    }
    if (isFree) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
      if (!emailOk) {
        toast.error("Please enter a valid email so we can send your PDF.");
        return;
      }
    }
    setLoading(true);
    try {
      const endpoint = isFree ? `${API}/pet-resume/generate` : `${API}/members/pet-resume/generate`;
      const body = isFree
        ? { ...form, email: email.trim(), first_name: firstName.trim(), consent }
        : { ...form, members_token: token };
      const res = await axios.post(endpoint, body, { responseType: "blob", timeout: 60000 });
      // Trigger download
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${form.pet_name.trim().replace(/\s+/g, "_")}_resume.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Pet resume downloaded — good luck out there!");
    } catch (err) {
      if (!isFree && err.response?.status === 403) {
        toast.error("Your members subscription isn't active.");
      } else if (!isFree && err.response?.status === 410) {
        toast.error("Members link expired — sign in again from /members.");
        setTimeout(() => (window.location.href = "/members"), 1500);
      } else {
        toast.error("Couldn't build your PDF — please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    if (!window.confirm("Clear the form and start over?")) return;
    setForm(EMPTY);
    localStorage.removeItem(DRAFT_KEY);
    toast.success("Draft cleared");
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="pet-resume-page">
      <header className="px-5 md:px-8 py-5 border-b border-[var(--line)] bg-[var(--paper)]">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <a href={isFree ? "/" : "/members"} className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]" data-testid="pet-resume-back">
            <Plane size={18} /> The Ditch Collective
          </a>
          <a href={isFree ? "/" : "/members"} className="text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] hover:text-[var(--ink)] inline-flex items-center gap-1">
            <ArrowLeft size={12} /> {isFree ? "Back home" : "Back to members"}
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-6 pt-10 pb-20">
        {isFree ? (
          <>
            {/* Free-tier sponsor banner */}
            <div className="rounded-2xl border-2 border-[var(--lime)] bg-[var(--lime)]/20 px-5 py-4 mb-6 flex items-start gap-3" data-testid="pet-resume-sponsor-banner">
              <Heart size={18} className="text-[var(--ink)] mt-0.5 shrink-0" />
              <div>
                <p className="font-display font-extrabold text-[var(--ink)] text-sm">
                  Free for July · sponsored by Jelly & Pretzel
                </p>
                <p className="text-xs text-[var(--ocean)] mt-1">
                  Two very good pups making sure every Kiwi dog crossing the ditch lands their dream rental. Go on — build one for your pet.
                </p>
              </div>
            </div>
            <span className="num-tag text-xs">Free tool · No signup needed</span>
          </>
        ) : (
          <span className="num-tag text-xs">Interactive guide · Members</span>
        )}
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-2 leading-tight" data-testid="pet-resume-title">
          Build your Pet Resume
        </h1>
        <p className="text-[var(--ocean)] mt-3 text-base leading-relaxed">
          Fill in the details below, drop in a cute photo, and we&apos;ll generate a branded
          PDF you can attach to every rental application. Saved as you type.
        </p>

        {/* Free-mode email capture */}
        {isFree && (
          <Section title="Your email" eyebrow="00">
            <p className="text-xs text-[var(--ocean)] mb-3">
              So we know where to send updates when we make this tool even better. No spam — cancel any time.
            </p>
            <Grid>
              <Field label="Your first name" testId="pet-resume-first-name" value={firstName} onChange={setFirstName} placeholder="Cara" />
              <Field label="Email" testId="pet-resume-email" value={email} onChange={setEmail} placeholder="you@example.co.nz" />
            </Grid>
            <label className="mt-4 flex items-start gap-2 text-xs text-[var(--ocean)] cursor-pointer" data-testid="pet-resume-consent">
              <input
                type="checkbox"
                className="mt-0.5 accent-[var(--lime-deep)]"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                Send me useful moving-to-Aus tips + let me know when new free tools drop. I can unsubscribe anytime.
              </span>
            </label>
          </Section>
        )}

        {/* About */}
        <Section title="About your pet" eyebrow="01">
          <Grid>
            <Field label="Pet name" testId="pet-name" value={form.pet_name} onChange={set("pet_name")} placeholder="Jelly" />
            <Field label="Breed" testId="pet-breed" value={form.breed} onChange={set("breed")} placeholder="Cavoodle" />
            <Field label="Age" testId="pet-age" value={form.age} onChange={set("age")} placeholder="3 years" />
            <Field label="Weight" testId="pet-weight" value={form.weight} onChange={set("weight")} placeholder="6 kg" />
            <Field label="Colour" testId="pet-colour" value={form.colour} onChange={set("colour")} placeholder="Black" />
            <Field label="Your name(s)" testId="pet-humans" value={form.humans} onChange={set("humans")} placeholder="Cara & Tom" />
          </Grid>

          {/* Photo */}
          <div className="mt-6">
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mb-2">Photo</label>
            <label className="block rounded-2xl border-2 border-dashed border-[var(--line)] hover:border-[var(--ink)] cursor-pointer bg-white transition-colors overflow-hidden" data-testid="pet-photo-upload">
              {form.photo_base64 ? (
                <img src={form.photo_base64} alt="Your pet" className="w-full max-h-72 object-cover" />
              ) : (
                <div className="py-12 text-center">
                  <Camera size={32} className="mx-auto text-[var(--ocean)]" />
                  <p className="mt-3 font-display font-bold text-[var(--ink)]">Add your pet&apos;s cutest photo</p>
                  <p className="text-xs text-[var(--ocean)] mt-1">PNG / JPG · up to 6MB</p>
                </div>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
            </label>
            {form.photo_base64 && (
              <button
                type="button"
                onClick={() => set("photo_base64")(null)}
                className="mt-2 text-xs uppercase tracking-widest font-bold text-[var(--ocean)] hover:text-[var(--ink)]"
              >
                Remove photo
              </button>
            )}
          </div>

          {/* Status */}
          <Subhead>Status</Subhead>
          <CheckGrid
            options={[
              ["microchipped", "Microchipped"],
              ["desexed", "Desexed"],
              ["council_registered", "Council Registered"],
              ["vaccinations_current", "Vaccinations Current"],
            ]}
            form={form}
            set={set}
          />

          {/* Personality */}
          <Subhead>Personality — tick all that apply</Subhead>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {PERSONALITY.map((opt) => (
              <CheckPill
                key={opt}
                label={opt}
                checked={form.personality.includes(opt)}
                onChange={() => togglePersonality(opt)}
                testId={`personality-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              />
            ))}
          </div>
        </Section>

        {/* Rental history */}
        <Section title="Rental history" eyebrow="02">
          <Grid>
            <Field label="Have you rented before?" testId="rented-before" value={form.rented_before} onChange={set("rented_before")} placeholder="Yes, in Wellington" />
            <Field label="How long?" testId="rental-duration" value={form.rental_duration} onChange={set("rental_duration")} placeholder="4 years" />
          </Grid>
          <Field label="Any damage?" testId="damage" value={form.damage} onChange={set("damage")} placeholder="None reported." mt={4} />
          <TextArea
            label="Previous property manager / landlord — name, phone, email"
            testId="previous-landlord"
            value={form.previous_landlord}
            onChange={set("previous_landlord")}
            placeholder="Sarah Smith · 021 234 567 · sarah@example.co.nz"
          />
        </Section>

        {/* Health */}
        <Section title="Health" eyebrow="03">
          <CheckGrid
            options={[
              ["vaccinated", "Vaccinated"],
              ["flea_treatments", "Flea Treatments Current"],
              ["worm_treatments", "Worm Treatments Current"],
              ["healthy", "Healthy"],
            ]}
            form={form}
            set={set}
          />
          <Field label="Vet clinic" testId="vet-clinic" value={form.vet_clinic} onChange={set("vet_clinic")} placeholder="Bondi Vet" mt={4} />
        </Section>

        {/* Why I'd love to live here */}
        <Section title="Why I'd love to live here" eyebrow="04">
          <p className="text-xs text-[var(--ocean)] italic mb-2">
            Sleeping all day? Playing fetch? Watching birds? Being best friends with everyone?
          </p>
          <TextArea
            label="Tell your future landlord about your pet"
            testId="why-love"
            value={form.why_love_to_live}
            onChange={set("why_love_to_live")}
            placeholder="I love long walks, lying in sunny spots, and being a quiet little shadow…"
            rows={4}
          />
        </Section>

        {/* References */}
        <Section title="References" eyebrow="05">
          <p className="text-xs text-[var(--ocean)] mb-4">
            A reference from a previous landlord and your vet makes a world of difference.
          </p>
          <RefBlock
            title="Landlord"
            prefix="landlord_ref"
            form={form}
            set={set}
          />
          <RefBlock
            title="Veterinarian"
            prefix="vet_ref"
            form={form}
            set={set}
          />
        </Section>

        {/* Action bar */}
        <div className="sticky bottom-0 -mx-5 md:-mx-6 mt-10 px-5 md:px-6 py-5 bg-[var(--cream)]/95 backdrop-blur-sm border-t border-[var(--line)] flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 z-10">
          <button
            type="button"
            onClick={reset}
            className="text-xs uppercase tracking-widest font-bold text-[var(--ocean)] hover:text-[var(--ink)] underline"
            data-testid="pet-resume-reset"
          >
            Clear & start over
          </button>
          <button
            type="button"
            onClick={generate}
            disabled={loading}
            data-testid="pet-resume-generate"
            className="inline-flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Building PDF…
              </>
            ) : (
              <>
                <Download size={18} /> Download my Pet Resume PDF
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}

// ── Building blocks ─────────────────────────────────────────────────────────
const Section = ({ title, eyebrow, children }) => (
  <section className="mt-10 bg-white rounded-3xl border border-[var(--line)] p-6 md:p-8 shadow-sm">
    <div className="flex items-baseline gap-3">
      <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--lime-deep)]">{eyebrow}</span>
      <h2 className="font-display text-xl font-extrabold text-[var(--ink)]">{title}</h2>
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

const Subhead = ({ children }) => (
  <p className="mt-6 mb-3 font-display text-sm font-extrabold text-[var(--ink)]">{children}</p>
);

const Grid = ({ children }) => (
  <div className="grid sm:grid-cols-2 gap-x-5 gap-y-4">{children}</div>
);

const Field = ({ label, testId, value, onChange, placeholder, mt = 0 }) => (
  <div className={mt ? `mt-${mt}` : ""}>
    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">{label}</label>
    <input
      data-testid={testId}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--ink)] transition-colors"
    />
  </div>
);

const TextArea = ({ label, testId, value, onChange, placeholder, rows = 3 }) => (
  <div className="mt-4">
    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">{label}</label>
    <textarea
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--ink)] transition-colors resize-y"
    />
  </div>
);

const CheckPill = ({ label, checked, onChange, testId }) => (
  <button
    type="button"
    onClick={onChange}
    data-testid={testId}
    className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${
      checked
        ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
        : "bg-white border-[var(--line)] hover:border-[var(--ink)]"
    }`}
  >
    <span
      className={`shrink-0 w-4 h-4 rounded-md border-2 flex items-center justify-center ${
        checked ? "bg-[var(--lime)] border-[var(--lime)]" : "bg-white border-[var(--line)]"
      }`}
    >
      {checked && <Check size={10} strokeWidth={3} className="text-[var(--ink)]" />}
    </span>
    <span>{label}</span>
  </button>
);

const CheckGrid = ({ options, form, set }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
    {options.map(([k, label]) => (
      <CheckPill
        key={k}
        label={label}
        checked={!!form[k]}
        onChange={() => set(k)(!form[k])}
        testId={`status-${k}`}
      />
    ))}
  </div>
);

const RefBlock = ({ title, prefix, form, set }) => (
  <div className="mt-4 first:mt-0 rounded-2xl border border-[var(--line)] bg-[var(--cream)] p-5">
    <p className="font-display font-extrabold text-[var(--ink)] mb-3">{title}</p>
    <Grid>
      <Field label="Name" testId={`${prefix}-name`} value={form[`${prefix}_name`]} onChange={set(`${prefix}_name`)} placeholder="" />
      <Field label="Phone" testId={`${prefix}-phone`} value={form[`${prefix}_phone`]} onChange={set(`${prefix}_phone`)} placeholder="" />
      <Field label="Email" testId={`${prefix}-email`} value={form[`${prefix}_email`]} onChange={set(`${prefix}_email`)} placeholder="" />
    </Grid>
    <TextArea label="Notes" testId={`${prefix}-notes`} value={form[`${prefix}_notes`]} onChange={set(`${prefix}_notes`)} placeholder="" rows={2} />
  </div>
);
